import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./styles/CollaborationSolutionsPage.css";

const valueCards = [
  {
    icon: "01",
    title: "Unified communication",
    text: "Bring voice, video meetings, team messaging, presence, and file sharing into one connected experience that is easier for teams to use and easier for businesses to manage.",
  },
  {
    icon: "02",
    title: "Partner-led growth",
    text: "Our Channel Partner Program helps you deliver modern communication solutions to clients while opening new opportunities for recurring value and stronger long-term relationships.",
  },
  {
    icon: "03",
    title: "Flexible deployment",
    text: "Support businesses of different sizes with scalable UCaaS solutions that can align with remote work, hybrid teams, branch offices, and evolving operational needs.",
  },
];

const capabilities = [
  {
    title: "Business calling",
    text: "Cloud-based voice services with dependable calling, routing, extensions, and business-friendly call handling.",
  },
  {
    title: "Video meetings",
    text: "Simple collaboration across teams, clients, and remote users with reliable meeting and conferencing tools.",
  },
  {
    title: "Team messaging",
    text: "Keep conversations organized with fast internal communication, shared channels, and integrated collaboration workflows.",
  },
  {
    title: "Mobility and remote access",
    text: "Support users across desktop and mobile so communication stays consistent from office, home, or while traveling.",
  },
];

const tabs = [
  {
    id: "partner-program",
    label: "Partner Program",
    title: "A communication offering that helps partners grow",
    desc: "The ComNetworks.net Channel Partner Program is designed for organizations that want to deliver modern collaboration solutions with confidence. It gives partners access to UCaaS capabilities that help clients modernize communication while creating new opportunities for service-led growth.",
    bullets: [
      "Expand your portfolio with modern collaboration services.",
      "Support client growth with reliable and scalable communication tools.",
      "Create stronger long-term value through recurring service opportunities.",
    ],
  },
  {
    id: "uCaas",
    label: "UCaaS Solutions",
    title: "Modern collaboration built around how teams actually work",
    desc: "Our UCaaS solutions combine voice, messaging, meetings, and collaboration tools into one connected environment. Instead of managing separate systems, businesses can simplify communication and improve day-to-day responsiveness across teams and locations.",
    bullets: [
      "Unify communication tools in one easier-to-manage platform.",
      "Improve responsiveness across hybrid and distributed teams.",
      "Reduce friction between internal collaboration and client communication.",
    ],
  },
  {
    id: "business-value",
    label: "Business Value",
    title: "Designed to improve communication and operational efficiency",
    desc: "Effective communication supports faster decisions, better customer experiences, and more efficient teamwork. Our collaboration solutions help organizations reduce communication silos, improve accessibility, and create a more consistent experience across the business.",
    bullets: [
      "Support faster decisions and smoother collaboration.",
      "Improve service consistency across locations and teams.",
      "Give clients a practical path to communication modernization.",
    ],
  },
];

const outcomes = [
  {
    title: "Simpler communication stack",
    text: "Replace fragmented tools with a unified platform that is easier to use, support, and scale.",
  },
  {
    title: "Better client experience",
    text: "Help clients communicate more clearly across voice, video, and messaging touchpoints.",
  },
  {
    title: "Support for hybrid work",
    text: "Enable consistent collaboration across office, remote, and mobile environments.",
  },
];

const featureList = [
  "Cloud-based calling and call routing",
  "Integrated messaging and team collaboration",
  "Video meetings and conferencing support",
  "Scalable communication for growing teams",
  "Partner-ready service delivery approach",
  "Flexible support for distributed workforces",
];

export default function CollaborationSolutionsPage() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);

  const selectedTab = useMemo(
    () => tabs.find((tab) => tab.id === activeTab) || tabs[0],
    [activeTab]
  );

  return (
    <div className="collabPage">
      <section className="collabHero">
        <div className="collabContainer collabHero__container">
          <div className="collabHero__content">
            <p className="collabHero__kicker">Collaboration Solutions</p>

            <h1 className="collabHero__title">
              Modern communication solutions built for business growth.
            </h1>

            <p className="collabHero__text">
              At ComNetworks.net, we help businesses and partners deliver better
              communication experiences through modern Unified Communications as
              a Service solutions. Our Collaboration Solutions are designed to
              simplify how teams connect, support flexible work, and create new
              opportunities for long-term client value.
            </p>

            <div className="collabHero__actions">
              <Link to="/contact" className="collabBtn collabBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="collabBtn collabBtn--ghost">
                Explore Services
              </Link>
            </div>

            <div className="collabHero__miniGrid">
              <div className="collabMiniCard">
                <span className="collabMiniCard__label">Focus</span>
                <strong>UCaaS & Collaboration</strong>
              </div>
              <div className="collabMiniCard">
                <span className="collabMiniCard__label">Built for</span>
                <strong>Partners & Modern Teams</strong>
              </div>
              <div className="collabMiniCard">
                <span className="collabMiniCard__label">Outcome</span>
                <strong>Better communication at scale</strong>
              </div>
            </div>
          </div>

          <div className="collabHero__visual">
            <div className="collabHero__orb collabHero__orb--one" />
            <div className="collabHero__orb collabHero__orb--two" />

            <div className="collabHero__panel">
              <div className="collabHero__panelTop">
                <span className="collabPill">Connected teams</span>
                <span className="collabPill collabPill--soft">Partner ready</span>
              </div>

              <h3 className="collabHero__panelTitle">
                Communication that works across voice, meetings, and messaging
              </h3>

              <p className="collabHero__panelText">
                Give clients a more connected communication environment with
                modern tools that support responsiveness, flexibility, and
                better collaboration across the business.
              </p>

              <div className="collabHero__stats">
                <div className="collabStat">
                  <strong>Unified tools</strong>
                  <span>Voice, chat, meetings</span>
                </div>
                <div className="collabStat">
                  <strong>Flexible access</strong>
                  <span>Office, remote, mobile</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="collabSection collabSection--white">
        <div className="collabContainer">
          <div className="collabSectionHead">
            <p className="collabSectionHead__eyebrow">Why this service matters</p>
            <h2 className="collabSectionHead__title">
              A better way to support communication-driven businesses
            </h2>
            <p className="collabSectionHead__text">
              Communication is central to productivity, service delivery, and
              client relationships. Our Collaboration Solutions help businesses
              move away from disconnected tools and toward a more unified,
              scalable, and business-ready communication environment.
            </p>
          </div>

          <div className="collabGrid3">
            {valueCards.map((card) => (
              <article key={card.title} className="collabCard">
                <div className="collabCard__number">{card.icon}</div>
                <h3 className="collabCard__title">{card.title}</h3>
                <p className="collabCard__text">{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="collabSection collabSection--muted">
        <div className="collabContainer collabSplit">
          <div className="collabSplit__left">
            <p className="collabSectionHead__eyebrow">Core capabilities</p>
            <h2 className="collabSectionHead__title">
              Practical collaboration features for modern teams
            </h2>
            <p className="collabSectionHead__text">
              Our solutions are designed to support daily communication needs
              without adding complexity. From business calling to messaging and
              meetings, the goal is to provide communication tools that are easy
              to use and aligned with real operational needs.
            </p>
          </div>

          <div className="collabFeatureList">
            {capabilities.map((item) => (
              <article key={item.title} className="collabFeatureItem">
                <div className="collabFeatureItem__icon">+</div>
                <div>
                  <h3 className="collabFeatureItem__title">{item.title}</h3>
                  <p className="collabFeatureItem__text">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="collabSection collabSection--white">
        <div className="collabContainer">
          <div className="collabSectionHead collabSectionHead--center">
            <p className="collabSectionHead__eyebrow">Service focus</p>
            <h2 className="collabSectionHead__title">
              Explore the solution from different angles
            </h2>
            <p className="collabSectionHead__text">
              Whether you are evaluating the partner opportunity, the UCaaS
              platform itself, or the business impact, this service is built to
              create practical and scalable communication value.
            </p>
          </div>

          <div className="collabTabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`collabTab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="collabTabPanel">
            <div className="collabTabPanel__content">
              <h3 className="collabTabPanel__title">{selectedTab.title}</h3>
              <p className="collabTabPanel__text">{selectedTab.desc}</p>

              <ul className="collabBulletList">
                {selectedTab.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>

              <Link to="/contact" className="collabBtn collabBtn--outline">
                Discuss This Service
              </Link>
            </div>

            <div className="collabTabPanel__side">
              <div className="collabChecklistCard">
                <h4 className="collabChecklistCard__title">Included focus areas</h4>
                <ul className="collabChecklistCard__list">
                  {featureList.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="collabSection collabSection--muted">
        <div className="collabContainer">
          <div className="collabOutcomeCard">
            <div className="collabOutcomeCard__intro">
              <p className="collabSectionHead__eyebrow">Business outcomes</p>
              <h2 className="collabSectionHead__title">
                What this can help your clients achieve
              </h2>
              <p className="collabSectionHead__text">
                Strong collaboration solutions do more than connect calls. They
                improve responsiveness, reduce communication friction, and
                support a more consistent experience across users, teams, and
                locations.
              </p>
            </div>

            <div className="collabGrid3 collabGrid3--compact">
              {outcomes.map((item) => (
                <article key={item.title} className="collabCard collabCard--soft">
                  <h3 className="collabCard__title">{item.title}</h3>
                  <p className="collabCard__text">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="collabCta">
        <div className="collabContainer collabCta__container">
          <div>
            <p className="collabSectionHead__eyebrow collabSectionHead__eyebrow--light">
              Next step
            </p>
            <h2 className="collabCta__title">
              Bring modern collaboration solutions to your clients with confidence
            </h2>
            <p className="collabCta__text">
              If you are exploring collaboration tools, evaluating UCaaS
              offerings, or looking to create more value through a channel
              partnership approach, ComNetworks.net can help you move forward
              with a solution that fits your business goals.
            </p>

            <div className="collabCta__actions">
              <Link to="/contact" className="collabBtn collabBtn--primary">
                Contact Us
              </Link>
              <Link to="/services" className="collabBtn collabBtn--ghostLight">
                View More Services
              </Link>
            </div>
          </div>

          <div className="collabCta__panel">
            <h3 className="collabCta__panelTitle">This service is ideal for</h3>
            <ul className="collabCta__panelList">
              <li>Businesses modernizing communication systems</li>
              <li>Partners expanding into collaboration-led solutions</li>
              <li>Organizations supporting hybrid and distributed teams</li>
              <li>Clients seeking scalable voice, messaging, and meeting tools</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
